import type * as React from "react";
import { Bot, Check, CheckCheck, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Message } from "@/lib/api-client";
import { MessageMarkdown } from "@/components/chat/MessageMarkdown";

interface ChatMessagesProps {
  messages: Message[];
  formatDate: (timestamp: number) => string;
  formatTime: (timestamp: number) => string;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

const getDeliveryIcon = (status?: string) => {
  switch (status) {
    case "read":
      return <CheckCheck className="w-3 h-3 text-primary" />;
    case "delivered":
      return <CheckCheck className="w-3 h-3" />;
    case "sent":
      return <Check className="w-3 h-3" />;
    case "failed":
      return <span className="text-xs text-destructive">!</span>;
    default:
      return <Clock className="w-3 h-3 animate-pulse" />;
  }
};

import { motion, AnimatePresence } from "framer-motion";

export function ChatMessages({ messages, formatDate, formatTime, messagesEndRef }: ChatMessagesProps) {
  if (messages.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No messages yet</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <AnimatePresence initial={false}>
        {messages.map((message, index) => {
          const prev = messages[index - 1];
          const showDate =
            index === 0 ||
            formatDate(prev?.createdAt || 0) !== formatDate(message.createdAt);

          return (
            <div key={message.id}>
              {showDate && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center my-6"
                >
                  <span className="px-4 py-1 bg-muted/50 rounded-full text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 border border-border/30">
                    {formatDate(message.createdAt)}
                  </span>
                </motion.div>
              )}

              <motion.div
                layout
                initial={{ opacity: 0, x: message.role === "assistant" ? 20 : -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ 
                  type: "spring", 
                  damping: 20, 
                  stiffness: 300,
                  layout: { duration: 0.2 } 
                }}
                className={cn(
                  "flex w-full mb-4 min-w-0",
                  message.role === "assistant" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "relative px-5 py-3 shadow-md max-w-[85%] sm:max-w-[75%] min-w-0 transition-all",
                    message.role === "assistant"
                      ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                      : "bg-white dark:bg-card border border-border/40 rounded-2xl rounded-tl-sm text-foreground",
                  )}
                >
                  <MessageMarkdown content={message.content} />
                  <div
                    className={cn(
                      "flex items-center gap-1.5 mt-1.5 text-[10px] font-medium tracking-wide select-none",
                      message.role === "assistant"
                        ? "text-primary-foreground/80 justify-end"
                        : "text-muted-foreground/60 justify-start",
                    )}
                  >
                    <span>{formatTime(message.createdAt)}</span>
                    {message.role === "assistant" && (
                      <span className="opacity-90">{getDeliveryIcon(message.deliveryStatus)}</span>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })}
      </AnimatePresence>

      <div ref={messagesEndRef} />
    </div>
  );
}
